import React, { memo, useCallback, useEffect, useState } from 'react'
import { User } from 'types/users'
import { getRolesToUser } from 'services/roles_to_users'
import { Role, RoleToUser } from 'types/roles'
import { Button, Spin, Transfer } from 'antd'
import { getRoles } from 'services/roles'
import { TransferDirection } from 'antd/es/transfer'
import { addRoleToUser, deleteRoleToUser } from 'services/roles_to_users'

type Props = {
    user: User
    onSubmit?: () => void
}

export const UserToRoles = memo((props: Props) => {
    const { user, onSubmit } = props

    const [loading, setLoading] = useState<boolean>(false)
    const [userRoles, setUserRoles] = useState<RoleToUser[]>([])
    const [roles, setRoles] = useState<Role[]>([])

    const [targetKeys, setTargetKeys] = useState<string[]>([])
    const [selectedKeys, setSelectedKeys] = useState<string[]>([])

    useEffect(() => {
        setLoading(true)
        Promise.all([getRolesToUser(user.id), getRoles()])
            .then(([userRoles, roles]) => {
                setUserRoles(userRoles)
                setRoles(roles)
            })
            .finally(() => {
                setLoading(false)
            })
    }, [user])

    useEffect(() => {
        setTargetKeys(userRoles.map(it => it.roleId.toString()))
    }, [userRoles])

    const onChange = useCallback((nextTargetKeys: string[], direction: TransferDirection, moveKeys: string[]) => {
        setTargetKeys(nextTargetKeys)
    }, [])

    const onSelectChange = useCallback((sourceSelectedKeys: string[], targetSelectedKeys: string[]) => {
        setSelectedKeys([...sourceSelectedKeys, ...targetSelectedKeys])
    }, [])

    const submit = useCallback(
        async (addKeys: string[], deleteKeys: string[]) => {
            setLoading(true)
            const { id: userId } = user

            try {
                for (let i = 0; i < deleteKeys.length; i++) {
                    const roleId = deleteKeys[i]
                    await deleteRoleToUser(userId, Number(roleId))
                }

                for (let i = 0; i < addKeys.length; i++) {
                    const roleId = addKeys[i]
                    await addRoleToUser(userId, Number(roleId))
                }
            } catch (e) {
            } finally {
                setLoading(false)
            }
        },
        [user]
    )

    const handleSave = useCallback(async () => {
        const targetKeysMap = targetKeys.reduce((acc, it) => {
            acc[it] = true
            return acc
        }, {} as Record<string, boolean>)

        const deleteRosterKeys = userRoles.reduce((acc, it) => {
            if (targetKeysMap[it.roleId]) {
                delete targetKeysMap[it.roleId]
            } else {
                acc.push(it.roleId.toString())
            }

            return acc
        }, [] as string[])

        const addPlayerKeys = Object.keys(targetKeysMap)

        submit(addPlayerKeys, deleteRosterKeys).then(() => {
            onSubmit && onSubmit()
        })
    }, [userRoles, submit, targetKeys, onSubmit])

    return (
        <Spin spinning={loading}>
            <Transfer
                dataSource={roles}
                targetKeys={targetKeys}
                selectedKeys={selectedKeys}
                onChange={onChange}
                onSelectChange={onSelectChange}
                rowKey={record => record.id.toString()}
                render={item => `${item.id} - ${item.name}`}
                listStyle={{ minWidth: '300px' }}
            />
            <Button type={'primary'} onClick={handleSave}>
                Save
            </Button>
        </Spin>
    )
})
